<template>
  <section class="down" :class="{ 'is-rtl': isRtl }" :dir="dir" aria-label="Download WikiBit APP">
    <div class="down-stage">
      <div class="down-bg" aria-hidden="true"></div>

      <div class="down-core">
        <div class="down-content">
          <div class="down-text-group">
            <h2 class="down-title" :class="{ 'is-small-locale-title': isSmallDownTitleLocale }">
              {{ t('82497') }}<br />{{ t('82498') }}
            </h2>
            <p class="down-subtitle">{{ t('82496') }}</p>
          </div>
          <div class="store-list">
            <a
              class="store-btn"
              aria-label="Download on the App Store"
              :href="storeLinks.appStoreUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click="trackDownloadApp('苹果下载')"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/07/08/6ca42752-4123-464f-ac23-2f51248f9c20.png"
                alt="App Store"
                loading="lazy"
                width="175"
                height="52"
              />
            </a>
            <a
              class="store-btn"
              aria-label="Get it on Google Play"
              :href="storeLinks.googlePlayUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click="trackDownloadApp('谷歌下载')"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/07/08/ca4e7247-aa00-44cc-b1c8-8dc4d0823e6e.png"
                alt="Google Play"
                loading="lazy"
                width="175"
                height="52"
              />
            </a>
          </div>
        </div>
        <!-- en   ===    https://resources1.interface003.com/web30/2026/07/02/29860c44-c583-48fc-98a3-a9861eb41d9f.png
        zh   ===    https://resources1.interface003.com/web30/2026/07/02/e69bcfdc-9cca-47ba-a1e5-0e752dc7b024.png
        zh-cn ===   https://resources1.interface003.com/web30/2026/07/02/42681036-9c30-470a-b784-d359e6451418.png -->
        <CommonImage
          :src="phoneLeftImageUrl"
          alt=""
          class="phone-image phone-left"
          loading="lazy"
          width="306"
          height="471"
        />
        <!-- zh-cn === https://resources1.interface003.com/web30/2026/07/02/75973266-6467-4532-8035-4772e25d3505.png
        en === https://resources1.interface003.com/web30/2026/07/02/7177f0a5-1465-4c9e-8b55-c2bfd2607219.png
        zh === https://resources1.interface003.com/web30/2026/07/02/27147af8-231f-4696-a63f-2b778a13e712.png -->
        <CommonImage
          :src="phoneRightImageUrl"
          alt=""
          class="phone-image phone-right"
          loading="lazy"
          width="242"
          height="423"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { AppDownloadLinks } from '../../composables/useDownApi'

const { t, locale } = useAppI18n()
const { dir, isRtl } = useRtl()
const { getAppDownloadLinks } = useDownApi()
const matomo = useMatomo()

const FALLBACK_DOWNLOAD_LINKS: AppDownloadLinks = {
  appStoreUrl: '',
  googlePlayUrl: '',
  androidApkUrl: '',
}

const downloadLinks = ref<AppDownloadLinks>(FALLBACK_DOWNLOAD_LINKS)
const downloadLinksRequest = ref<Promise<AppDownloadLinks> | null>(null)

const storeLinks = computed(() => downloadLinks.value)
const isSmallDownTitleLocale = computed(() => ['ar', 'es', 'fil', 'ru', 'de', 'fr'].includes(locale))
const resolveLocaleImageUrl = (imageUrls: Record<'zh-cn' | 'zh' | 'en', string>) => {
  const languageCode = String(locale || '').toLowerCase()

  if (languageCode === 'zh-cn') {
    return imageUrls['zh-cn']
  }

  if (languageCode === 'zh' || languageCode === 'zh-hk' || languageCode === 'zh-tw') {
    return imageUrls.zh
  }

  return imageUrls.en
}
const phoneLeftImageUrl = computed(() =>
  resolveLocaleImageUrl({
    'zh-cn': 'https://resources1.interface003.com/web30/2026/08/03/0d6a7a11-4391-4b0b-9049-cd95e83e4644.png',
    zh: 'https://resources1.interface003.com/web30/2026/08/03/a5b3beb0-ce86-474b-8bf2-06e5085f1730.png',
    en: 'https://resources1.interface003.com/web30/2026/08/03/1b6fbd0d-d83b-426a-a873-d8058f012bf8.png',
  }),
)
const phoneRightImageUrl = computed(() =>
  resolveLocaleImageUrl({
    'zh-cn': 'https://resources1.interface003.com/web30/2026/07/20/9986da6d-60ba-4abb-a8aa-42a8ef14db6b.png',
    zh: 'https://resources1.interface003.com/web30/2026/07/20/1ef082c5-5369-420a-9d91-abc16948a55d.png',
    en: 'https://resources1.interface003.com/web30/2026/07/20/c077c4f5-0c55-454b-96f2-a06f6ff4b515.png',
  }),
)

const trackDownloadApp = (type: string) => {
  matomo.trackEvent('home_download_app', {
    af_type: type,
  })
}

const resolveDownloadLinks = async () => {
  if (!downloadLinksRequest.value) {
    downloadLinksRequest.value = getAppDownloadLinks()
      .then((response) => {
        downloadLinks.value = {
          appStoreUrl: response?.appStoreUrl || FALLBACK_DOWNLOAD_LINKS.appStoreUrl,
          googlePlayUrl: response?.googlePlayUrl || FALLBACK_DOWNLOAD_LINKS.googlePlayUrl,
          androidApkUrl: response?.androidApkUrl || FALLBACK_DOWNLOAD_LINKS.androidApkUrl,
        }

        return downloadLinks.value
      })
      .catch(() => {
        downloadLinks.value = FALLBACK_DOWNLOAD_LINKS
        return FALLBACK_DOWNLOAD_LINKS
      })
      .finally(() => {
        downloadLinksRequest.value = null
      })
  }

  return await downloadLinksRequest.value
}

onMounted(() => {
  resolveDownloadLinks()
})
</script>

<style scoped lang="less">
.down {
  --down-scale: 1;
  width: 100%;
  min-height: calc(471px * var(--down-scale));
  overflow: hidden;
  position: relative;
  isolation: isolate;
  background: #f8f8f8;
  .down-stage {
    position: relative;
    left: 50%;
    width: 1200px;
    min-height: 471px;
    margin-bottom: calc((var(--down-scale) - 1) * 471px);
    transform: translateX(-50%) scale(var(--down-scale));
    transform-origin: top center;
  }

  .down-bg {
    position: absolute;
    top: 82px;
    bottom: 0;
    left: 50%;
    width: calc(100vw / var(--down-scale));
    min-width: calc(800px / var(--down-scale));
    overflow: hidden;
    transform: translateX(-50%);
    pointer-events: none;
    background:
      radial-gradient(
        ellipse 60% 50% at 105% 112%,
        rgba(48, 118, 235, 0.95) 0%,
        rgba(12, 65, 155, 0.72) 38%,
        rgba(0, 35, 90, 0.32) 70%,
        transparent 100%
      ),
      radial-gradient(
        ellipse 32% 72% at 49% 54%,
        rgba(22, 40, 84, 0.9) 0%,
        rgba(26, 44, 88, 0.68) 38%,
        rgba(7, 24, 60, 0.12) 72%,
        transparent 100%
      ),
      linear-gradient(110deg, #07122f 0%, #07132f 26%, #07193f 62%, #071b45 100%);

    &::after {
      position: absolute;
      inset: 0;
      content: '';
      pointer-events: none;
      background: rgba(17, 45, 115, 0.4);
    }
  }

  .down-core {
    position: relative;
    min-height: 471px;
    width: 1200px;
    margin: 0 auto;
    z-index: 1;
  }

  .down-content {
    position: absolute;
    top: 82px;
    left: 0;
    width: 565px;
    height: 389px;
    min-height: 0;
    box-sizing: border-box;
    padding: 0 0 0 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 40px;
    z-index: 2;
  }

  .down-text-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .down-title {
    margin: 0;
    color: #fff;
    font-size: 40px;
    font-weight: 600;
    line-height: 50px;
    word-break: break-word;
    overflow-wrap: anywhere;

    &.is-small-locale-title {
      font-size: 28px;
    }
  }

  .down-subtitle {
    margin: 0;
    color: #dddee1;
    font-size: 24px;
    font-weight: 400;
    line-height: normal;
  }

  .store-list {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .store-btn {
    width: 175.499px;
    height: 52px;
    border: none;
    padding: 0;
    background: transparent;
    cursor: pointer;

    :deep(.main-image) {
      width: 100%;
      height: auto !important;
      display: block;
    }
  }

  .phone-image {
    position: absolute;
    display: block;
    object-fit: cover;
    object-position: bottom;
    pointer-events: none;
    z-index: 2;
  }

  .phone-left {
    bottom: 0;
    left: 611px;
    width: 306px;
    height: 471px;
  }

  .phone-right {
    bottom: 0;
    left: 933px;
    width: 236px;
  }

  &.is-rtl {
    .down-stage {
      right: 50%;
      left: auto;
      transform: translateX(50%) scale(var(--down-scale));
    }

    .down-content {
      margin-left: auto;
      right: 0;
      left: auto;
      padding: 0 6px 0 0;
      text-align: right;
    }

    .store-list {
      direction: ltr;
      justify-content: flex-end;
    }

    .phone-left {
      right: 611px;
      left: auto;
    }

    .phone-right {
      right: 933px;
      left: auto;
    }
  }
}

@media screen and (max-width: 1200px) and (min-width: 801px) {
  .down {
    --down-scale: clamp(0.6666667, calc((100vw - 48px) / 1200px), 1);
  }
}

@media screen and (max-width: 800px) {
  .down {
    --down-scale: 0.6666667;
    width: 800px;
    min-width: 800px;
    max-width: none;
  }
}
</style>
